import React, { useEffect } from "react";
import {
  Stepper,
  Step,
  StepLabel,
  useMediaQuery,
  Theme,
  useTheme,
  MobileStepper,
  Button,
  Typography,
} from "@mui/material";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";

import { useStyles } from "./UploadStepper.style";

export type UploadStep = {
  label: string;
  text: string;
};

interface UploadStepperProps {
  activeStep: number;
  steps: UploadStep[];
  onBack: () => void;
}

export const UploadStepper: React.FC<UploadStepperProps> = ({
  activeStep,
  steps,
  onBack,
  ...props
}) => {
  const maxSteps = steps.length;
  const isFirst = activeStep === 0;
  const isLast = activeStep === steps.length - 1;
  const styles = useStyles();
  const theme = useTheme();
  const isXsmall = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down("xs")
  );
  const { t } = useTranslation();
  const { isValid, dirty, isSubmitting, validateForm } = useFormikContext();

  useEffect(() => {
    const validate = async () => {
      await validateForm();
    };
    validate();
  }, [activeStep, dirty, isValid, isSubmitting, validateForm]);

  return (
    <>
      <div className={styles.root}>
        {!isXsmall ? (
          <div>
            <div>
              <Stepper activeStep={activeStep} alternativeLabel className={styles.stepper}>
                {steps.map((step, index) => (
                  <Step key={index}>
                    <StepLabel>{step.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </div>
            <div>
              <Typography className={styles.instructions}>
                {steps[activeStep].text}
              </Typography>
            </div>
            {props.children}
            <div className={styles.stepperButtons}>
              <Button
                disabled={isFirst}
                variant="contained"
                onClick={onBack}
                className={styles.backButton}
              >
                {t("button.back")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={!isValid || !dirty || isSubmitting}
                className={styles.nextButton}
              >
                {isLast ? t("button.upload") : t("button.next")}
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div>
              <Typography className={styles.instructions}>
                {steps[activeStep].text}
              </Typography>
            </div>
            {props.children}
            <MobileStepper
              className={styles.stepperButtons}
              steps={maxSteps}
              position="static"
              variant="dots"
              activeStep={activeStep}
              nextButton={
                <Button
                  size="small"
                  type="submit"
                  disabled={!isValid || !dirty || isSubmitting}
                >
                  {isLast ? t("button.upload") : t("button.next")}
                  {theme.direction === "rtl" ? (
                    <KeyboardArrowLeft />
                  ) : (
                    <KeyboardArrowRight />
                  )}
                </Button>
              }
              backButton={
                <Button
                  size="small"
                  onClick={onBack}
                  disabled={activeStep === 0}
                >
                  {theme.direction === "rtl" ? (
                    <KeyboardArrowRight />
                  ) : (
                    <KeyboardArrowLeft />
                  )}
                  {t("button.back")}
                </Button>
              }
            />
          </div>
        )}
      </div>
    </>
  );
};
